import type {
  MenuType,
  PublishData,
  PublishMenu,
  PublishResponse,
} from '@storeOld/reducers/publish'
import slugify from 'slugify'

const generateSlug = (name: string, uniqIndex?: number) => {
  const trimmedName = name.substring(0, 50)

  return `${slugify(trimmedName || 'Page', {
    lower: true,
    strict: true,
  })}-${uniqIndex}`
}

const addSlugs = (data: MenuType[], parentSlug = '', sectionIndex: number) => {
  return data.map((item, index) => {
    item.slug =
      index === 0 && parentSlug === '' && sectionIndex === 0
        ? '/'
        : `${parentSlug}/${generateSlug(item.name, item.uniqIndex)}`.replace(
            /\/+/g,
            '/'
          )

    if (item.children && item.children.length > 0) {
      addSlugs(item.children, item.slug, sectionIndex)
    }

    return item
  })
}

export const buildMenuTree = (
  data: {
    id: string
    title: string
    parentId: string | null
    uniqIndex?: number
  }[],
  sectionIndex: number
) => {
  const itemMap: Record<string, MenuType> = {}

  data.forEach((item) => {
    const depth = item.parentId ? (itemMap[item.parentId]?.depth ?? 0) + 1 : 0
    itemMap[item.id] = {
      id: item.id,
      name: item.title || 'Page',
      uniqIndex: item.uniqIndex,
      children: [],
      depth,
    }
  })

  const menuTree: MenuType[] = []

  data.forEach((item) => {
    const currentItem = itemMap[item.id]

    if (item.parentId && itemMap[item.parentId]) {
      const parent = itemMap[item.parentId]
      currentItem.depth = parent.depth + 1
      parent.children.push(currentItem)
    } else {
      menuTree.push(currentItem)
    }
  })

  function sortByChildIndex(node: any) {
    if (node.children.length > 0) {
      node.children.forEach(sortByChildIndex)
    }
  }

  menuTree.forEach(sortByChildIndex)

  return addSlugs(menuTree, '', sectionIndex)
}

export const flattenArray = (arr: PublishMenu[]) => {
  const result: {
    id: string
    slug?: string
  }[] = []

  function recursiveFlatten(items: MenuType[]) {
    items.forEach((item) => {
      result.push({
        id: item.id,
        slug: item.slug,
      })

      if (item.children && item.children.length > 0) {
        recursiveFlatten(item.children)
      }
    })
  }

  arr.forEach((a) => {
    recursiveFlatten(a.items)
  })

  return result.reduce(
    (acc, item) => {
      acc[item.id] = item.slug || null

      return acc
    },
    {} as Record<string, string | null>
  )
}

export const publishResponseAdapter = (data: PublishResponse): PublishData => {
  return {
    id: data.id,
    projectId: data.projectUuid,
    status: data.status,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    tab: data.tab,
    searchEngine: data.searchEngine,
    withBranding: data.withBranding,
    pagePreview: data.pagePreview,
    contentType: data.contentType,
    fontType: data.fontType,
    bgColor: data.bgColor,
    presetName: data.presetName,
    theme: data.theme,
  }
}

export const getUrl = (id?: string | null) => {
  return `https://public-${id}.${import.meta.env.VITE_PUBLISH_LOCATION_HOST}`
}
